import { useState, useCallback, useRef, useMemo } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  setDoc,
  writeBatch,
  limit,
  QueryDocumentSnapshot,
  Firestore,
  startAfter 
} from "firebase/firestore";
import { Message, ChatPayload, QuickReply } from "../utils/types";
import { createAiActDisclosureMessage } from "../utils/aiActDisclosure";

// Helper function to generate a consistent ID from message content to aid in deduplication.
const idFromContent = (msg: Partial<Message>) => {
  const str = JSON.stringify({
    isUser: msg.isUser,
    text: msg.text ?? "",
    card: msg.card ?? null,
    cards: msg.cards ?? null,
    chips: msg.suggestionChips ?? null,
    multiChips: msg.multiQuickReplies ?? null,
  });
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return `msg_${hash}`;
};

// Helper function to create a stable string representation of a message for comparison.
const stableStringify = (val: any): string => {
  if (val === null || typeof val !== "object") {
    return JSON.stringify(val);
  }
  if (Array.isArray(val)) {
    return "[" + val.map(stableStringify).join(",") + "]";
  }
  const keys = Object.keys(val).sort();
  return (
    "{" +
    keys
      .map((k) => {
        const v = (val as any)[k];
        return JSON.stringify(k) + ":" + stableStringify(v);
      })
      .join(",") +
    "}"
  );
};

// Creates a content key to check if a new message is a duplicate of the last one.
const contentKey = (msg: Partial<Message>) =>
  stableStringify({
    isUser: msg.isUser,
    text: msg.text ?? "",
    card: msg.card ?? null,
    cards: msg.cards ?? null,
    chips: msg.suggestionChips ?? null,
    multiChips: msg.multiQuickReplies ?? null,
  });

const isDuplicateOfLast = (arr: Message[], msg: Partial<Message>) => {
  const last = arr[arr.length - 1];
  return last ? contentKey(last) === contentKey(msg) : false;
};

const generateId = () => Date.now() + Math.random();

interface UseChatProps {
  userId: string;
  db: Firestore;
  onResponseEnd: () => void;
}

export const useChat = ({ userId, db, onResponseEnd }: UseChatProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [oldestDoc, setOldestDoc] = useState<QueryDocumentSnapshot | null>(
    null
  );
  const isFirstTokenRef = useRef(true);
  const [pendingResponses] = useState(0);
  const streamEndTimerRef = useRef<NodeJS.Timeout | null>(null);
  const tokenIdleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const streamingMessageIdRef = useRef<number | string | null>(null);
  const streamBufferRef = useRef<string>("");
  const lastTokenRef = useRef<string | null>(null);
  const lastTokenAtRef = useRef<number>(0);
  const lastChatTextRef = useRef<string | null>(null);
  const receivedFinalRef = useRef<boolean>(false);
  const receivedFinalAtRef = useRef<number>(0);

  // Validate userId to prevent Firebase errors
  const validUserId = userId && userId.trim() !== "" ? userId : "guest_default";

  const userCollection = useMemo(
    () => collection(db, "messages", validUserId, "chat"),
    [db, validUserId]
  );

  const saveMessage = useCallback(
    async (msg: Message) => {
      if (msg.suggestionChips) return;
      try {
        const ref = doc(userCollection, idFromContent(msg));
        await setDoc(ref, msg);
      } catch (e) {
        console.error("❌ Failed to save message:", e);
      }
    },
    [userCollection]
  );

// Aggiungi questo nuovo ref nel componente useChat (dopo gli altri ref):
const waitingForChatAfterNotificationRef = useRef<boolean>(false);
const finalizedStreamIdRef = useRef<number | string | null>(null);

const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        console.log("[WS] incoming", {
          type: data?.type,
          when: data?.when,
          hasContent: typeof data?.content === "string" ? data.content.slice(0, 60) : undefined,
          token: typeof data?.token === "string" ? data.token : undefined,
          delta: typeof data?.delta === "string" ? data.delta : undefined,
        });

        const isNotification = data.type === "notification";
        const isChat = data.type === "chat";
        const isChatToken = data.type === "chat_token";

          if (isChat || isNotification || data.type === 'carousel') {
            // PRIMA: Cancella tutti i timer esistenti
            if (streamEndTimerRef.current) clearTimeout(streamEndTimerRef.current);
            if (tokenIdleTimerRef.current) clearTimeout(tokenIdleTimerRef.current);
            
            if (isNotification) {
              // Per notification: blocca e segnala che stiamo aspettando il messaggio chat
              setIsStreaming(true);
              setIsTyping(true);
              waitingForChatAfterNotificationRef.current = true; // Importante!
            } else if (isChat || data.type === 'carousel') {
              // Per chat o carousel: sblocca solo se non stiamo aspettando dopo una notification
              if (waitingForChatAfterNotificationRef.current) {
                // Questo è il messaggio chat che segue la notification
                waitingForChatAfterNotificationRef.current = false;
                setIsStreaming(false);
                setIsTyping(false);
                onResponseEnd();
              } else {
                // Messaggio chat normale o carousel
                setIsStreaming(false);
                setIsTyping(false);
                onResponseEnd();
              }
            }
          }
  
          
  
          if (isChatToken) {
            // Apply a short cooldown after a final to ignore trailing tokens from the same turn
            const now = Date.now();
            if (receivedFinalRef.current && now - receivedFinalAtRef.current < 300) {
              console.log("[STREAM] ignore token after final (cooldown)");
              return;
            }
            receivedFinalRef.current = false;
            receivedFinalAtRef.current = 0;
            lastChatTextRef.current = null;
            
            setIsStreaming(true);
            setIsTyping(false);
  
            const token: string =
              typeof data.content === "string"
                ? data.content
                : typeof (data.token ?? data.delta) === "string"
                ? (data.token ?? data.delta)
                : "";
  
            if (streamingMessageIdRef.current == null) {
              streamingMessageIdRef.current = generateId();
              streamBufferRef.current = "";
            }
  
            if (isFirstTokenRef.current) {
            setIsStreaming(true);
            setIsTyping(false);
            onResponseEnd(); // Segnala l'inizio della risposta
            isFirstTokenRef.current = false;
          }
  
            
  
            if (token) {
              const now2 = Date.now();
              if (lastTokenRef.current === token && now2 - lastTokenAtRef.current < 100) {
                console.log("[STREAM] skip duplicate token", { token });
              } else {
                lastTokenRef.current = token;
                lastTokenAtRef.current = now2;
                streamBufferRef.current = (streamBufferRef.current || "") + token;
              }
  
              setMessages((prev) => {
                const id = streamingMessageIdRef.current as number | string;
                const newText = streamBufferRef.current || "";
                const updated = [...prev];
                let idx = updated.findIndex((m) => m.id === id);
  
                if (idx === -1) {
                  const newMsg: Message = { id, isUser: false, text: "", streaming: true, timestamp: new Date() };
                  updated.push(newMsg);
                  idx = updated.length - 1;
                }
                const msg = updated[idx];
                updated[idx] = { ...msg, text: newText };
                return updated;
              });
            }
            
            if (tokenIdleTimerRef.current) clearTimeout(tokenIdleTimerRef.current);
            tokenIdleTimerRef.current = setTimeout(() => {
              setIsStreaming(false);
              setIsTyping(false);
              onResponseEnd();
              setMessages((prev) => {
                const id = streamingMessageIdRef.current;
                if (id == null) return prev;
                const updated = [...prev];
                const idx = updated.findIndex((m) => m.id === id);
                if (idx !== -1 && (updated[idx].text || "").length > 0) {
                  saveMessage(updated[idx]);
                }
                return updated;
              });
              streamingMessageIdRef.current = null;
              streamBufferRef.current = "";
              lastTokenRef.current = null;
              lastTokenAtRef.current = 0;
              receivedFinalRef.current = false;
            }, 1200);
            return;
          }
  
          // Handle 'chat' and 'notification' types (non-streaming final messages)
          if (isChat || isNotification || data.type === 'carousel') {
            receivedFinalRef.current = true;
            receivedFinalAtRef.current = Date.now();
            isFirstTokenRef.current = true;
  
            let content = data.content;
            let parsed: ChatPayload | ChatPayload[] | null = null;
  
            if(content && typeof content === "object") {
              parsed = content as ChatPayload | ChatPayload[];
            } else if (data.type === 'carousel' && Array.isArray(data.images)) {
              // Gestione specifica per il carosello di immagini
              parsed = { type: 'carousel', images: data.images } as any;
            }
            
            if (typeof content === "string") {
              try {
                let str = content.trim();
                if ((str.startsWith("{") && str.endsWith("}")) || (str.startsWith("[") && str.endsWith("]"))) {
                  parsed = JSON.parse(str);
                }
              } catch (e) {
                console.error("💥 Failed to parse JSON content:", e);
              }
            }
  
            setMessages((prev) => {
              // Rimuovi sia i messaggi isTyping che quelli streaming
              const updated = prev.filter((m) => !m.isTyping && !m.streaming);
              const timestamp = new Date();
              
              const handlePayload = (payload: any) => {
                // Handle image carousel
                if (payload && payload.type === 'carousel' && Array.isArray(payload.images)) {
                const imageUrls = payload.images
                  .map((img: any) => img.url)
                  .filter((url: string) => typeof url === 'string' && url.trim() !== '');
                
                if (imageUrls.length > 0) {
                  const imageMsg: Message = { 
                    id: generateId(), 
                    isUser: false, 
                    images: imageUrls, 
                    timestamp 
                  };
                  if (!isDuplicateOfLast(updated, imageMsg)) {
                    updated.push(imageMsg);
                    saveMessage(imageMsg);
                    console.log("[IMMAGINI] Messaggio con immagini aggiunto:", imageMsg);
                    // Skip processing other content for this payload since we only want images
                    return;
                  }
                }
              }

              // Handle text content
              if (typeof payload.text === "string") {
                const textRaw = String(payload.text).trim();
                if (textRaw) {
                  const textMsg: Message = { id: generateId(), isUser: false, text: textRaw, timestamp };
                  if (!isDuplicateOfLast(updated, textMsg)) {
                    updated.push(textMsg);
                    saveMessage(textMsg);
                  }
                }
              }
            
              // If there is no text in the payload but we have a live streamed text, finalize it
              if (!(payload && typeof payload.text === "string")) {
                if (streamingMessageIdRef.current != null) {
                  const idx = updated.findIndex((m) => m.id === streamingMessageIdRef.current);
                  if (idx !== -1 && (updated[idx].text || "").length > 0 && updated[idx].streaming) {
                    updated[idx] = { ...updated[idx], streaming: false, timestamp };
                    console.log("[STREAM] finalize existing streaming message (cards-only payload)", { id: updated[idx].id, textLen: (updated[idx].text || "").length });
                    saveMessage(updated[idx]);
                    finalizedStreamIdRef.current = updated[idx].id;
                  }
                }
              }
            
              // Handle cards
              const elements = 
              (payload as any)?.card?.attachment?.payload?.elements ||
              (payload as any)?.cards?.attachment?.payload?.elements ||
              (payload as any)?.json_card?.attachment?.payload?.elements;
              if (Array.isArray(elements)) {
                const cardMsg: Message = { id: generateId(), isUser: false, timestamp };
                if (elements.length === 1) cardMsg.card = elements[0];
                else if (elements.length > 1) cardMsg.cards = elements;
                if (!isDuplicateOfLast(updated, cardMsg)) {
                  updated.push(cardMsg);
                  saveMessage(cardMsg);
                }
              }
            
              // Handle suggestion chips
              if (payload?.quick_replies) {
                const chipMsg: Message = {
                  id: generateId(),
                  isUser: false,
                  suggestionChips: payload.quick_replies.map((chip: QuickReply) => ({
                    title: chip.title,
                    payload: chip.payload,
                  })),
                  timestamp,
                };
                if (!isDuplicateOfLast(updated, chipMsg)) {
                  updated.push(chipMsg);
                }
              }

              // Handle multi suggestion chips
              if (payload?.multi_quick_replies) {
                const chipMsg: Message = {
                  id: generateId(),
                  isUser: false,
                  multiQuickReplies: payload.multi_quick_replies.map((chip: QuickReply) => ({
                    title: chip.title,
                    payload: chip.payload,
                    is_selected: chip.is_selected,
                  })),
                  timestamp,
                };
                if (!isDuplicateOfLast(updated, chipMsg)) {
                  updated.push(chipMsg);
                }
              }
            };

            if (Array.isArray(parsed)) {
              parsed.forEach(handlePayload);
            } else if (parsed) {
              handlePayload(parsed);
            } else if (typeof content === "string") {
              const textRaw = String(content).trim();
              if (textRaw) {
                const textMsg: Message = { id: generateId(), isUser: false, text: textRaw, timestamp };
                if (!isDuplicateOfLast(updated, textMsg)) {
                  updated.push(textMsg);
                  saveMessage(textMsg);
                }
              }
            }
            return updated;
          });

          // Reset streaming refs immediately
          streamingMessageIdRef.current = null;
          streamBufferRef.current = "";
          lastTokenRef.current = null;
          lastTokenAtRef.current = 0;
          receivedFinalRef.current = false;
        }
      } catch (err) {
        console.error("❌ Errore nel parsing della risposta:", err);
        // In caso di errore, reset dello stato per evitare blocchi permanenti
        waitingForChatAfterNotificationRef.current = false;
        setIsStreaming(false);
        setIsTyping(false);
      }
    },
    [saveMessage, onResponseEnd]
  );
  
  const clearFirestoreMessages = useCallback(async () => {
    try {
      const batchSize = 500;
      while (true) {
        const q = query(userCollection, limit(batchSize));
        const snapshot = await getDocs(q);
        if (snapshot.empty) break;
        const batch = writeBatch(db);
        snapshot.docs.forEach((docSnap) => {
          batch.delete(docSnap.ref);
        });
        await batch.commit();
      }
      const disclosureMessage = createAiActDisclosureMessage();
      setMessages([disclosureMessage]);
      setOldestDoc(null);
      setHasMore(false);
      await saveMessage(disclosureMessage);
    } catch (e) {
      console.error("❌ Failed to clear Firestore messages:", e);
    }
  }, [userCollection, db, saveMessage]);

const loadMessages = useCallback(async () => {
  try {
    const q = query(userCollection, orderBy("timestamp", "desc"), limit(20));
    const snapshot = await getDocs(q);
    const loaded: Message[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.timestamp?.toDate) {
        data.timestamp = data.timestamp.toDate();
      }
      if (data.suggestionChips === undefined) {
        loaded.push(data as Message);
      }
    });

    if (!snapshot.empty) {
      setOldestDoc(snapshot.docs[snapshot.size - 1]);
    } else {
      setOldestDoc(null);
    }
    setHasMore(snapshot.size === 20);

    if (loaded.length === 0) {
      const disclosureMessage = createAiActDisclosureMessage();
      setMessages([disclosureMessage]);
      await saveMessage(disclosureMessage);
      return;
    }

    // Ordina i messaggi in ordine cronologico (dal più vecchio al più recente)
    setMessages(loaded.reverse());
  } catch (e) {
    console.error("❌ Failed to load messages:", e);
  }
}, [userCollection, saveMessage]);


const loadMoreMessages = useCallback(async () => {
  if (!oldestDoc) return;
  try {
    const q = query(
      userCollection,
      orderBy("timestamp", "desc"),
      startAfter(oldestDoc),
      limit(50)
    );
    const snapshot = await getDocs(q);
    const older: Message[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.timestamp?.toDate) {
        data.timestamp = data.timestamp.toDate();
      }
      if (data.suggestionChips === undefined) {
        older.push(data as Message);
      }
    });
    
    if (!snapshot.empty) {
      setOldestDoc(snapshot.docs[snapshot.size - 1]);
    }
    setHasMore(snapshot.size === 50);

    // Preponi i messaggi caricati all'inizio dell'array, dopo averli invertiti
    setMessages((prev) => [...older.reverse(), ...prev]);

  } catch (e) {
    console.error("❌ Failed to load more messages:", e);
  }
}, [userCollection, oldestDoc]);


  const sendMessage = useCallback(
    (
      ws: WebSocket | null,
      msg: string,
      backendPayload?: string,
      isFromChip: boolean = false
    ) => {
      setIsTyping(true);

      if (!msg.trim() || !ws || ws.readyState !== WebSocket.OPEN) {
        if (ws?.readyState !== WebSocket.OPEN) {
          console.error("Cannot send message: WebSocket is not open");
        }
        setIsTyping(false); // Ensure typing indicator is off on failure
        return;
      }

      const timestamp = new Date();
      const userMsg: Message = {
        id: generateId(),
        isUser: true,
        text: msg,
        timestamp,
      };

      setMessages((prev) => {
        const messagesWithoutChips = prev.filter((m) => !m.suggestionChips);
        return [...messagesWithoutChips, userMsg];
      });
      
      saveMessage(userMsg);

      const payloadToSend = backendPayload || msg;
      ws.send(JSON.stringify({ text: payloadToSend }));
    },
    [saveMessage]
  );

  const inputDisabled = isStreaming || isTyping;

  return {
    messages,
    isStreaming,
    isTyping,
    hasMore,
    inputDisabled,
    loadMessages,
    loadMoreMessages,
    clearFirestoreMessages,
    handleMessage,
    sendMessage,
    pendingResponses,
    setMessages,
  };
};
